import {
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { MetaService } from '@app/shared/services/meta.service';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { WindowUtils } from '@app/shared/utils/window.util';
import { AkitaConfigurationQuery } from '@app/akita/configuration/state/configuration.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { FooterComponent } from '../shared/components/new-footer/footer.component';
import { BidiModule } from '@angular/cdk/bidi';
import { AsyncPipe } from '@angular/common';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { OtherTopicsComponent } from '../faq-page/components/other-topics/other-topics.view';

@Component({
  selector: 'app-credit-installment-plans',
  templateUrl: './credit-installment-plans.template.html',
  styleUrls: ['./credit-installment-plans.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BidiModule,
    FooterComponent,
    AsyncPipe,
    TranslocoModule,
    OtherTopicsComponent,
  ],
})
export class CreditInstallmentPlansComponent implements OnDestroy, AfterViewInit {
  public lang: string;
  public country: string;

  public lessThan$: Observable<ScreenSizeInfoState>;
  public inFullScreenMode$: Observable<boolean>;

  private readonly langChangesObserver: Subscription | null;

  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly meta: MetaService,
    private readonly translateService: TranslocoService,
    private readonly akitaConfigurationQuery: AkitaConfigurationQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly detectChanges: ChangeDetectorRef
  ) {
    this.destroySubject = new Subject();

    this.lessThan$ = this.akitaScreenQuery.selectLessThan();

    this.inFullScreenMode$ = this.akitaRouterQuery.selectInFullScreenMode();

    this.country = this.akitaProductsQuery.validateLocale().country;

    this.langChangesObserver = this.translateService.langChanges$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          const description = this.translateService.translate('app_title');
          this.meta.setTitle('title_terms_of_use', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      });

    const languageCode = this.akitaConfigurationQuery.languageCode;
    this.lang = (languageCode.locale || 'EN').toUpperCase();
  }

  public ngAfterViewInit(): void {
    this.country = this.akitaProductsQuery.country;
    this.detectChanges.detectChanges();
    this.detectChanges.markForCheck();
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.langChangesObserver) {
      this.langChangesObserver.unsubscribe();
    }
  }
}
